import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const ClientList = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getPortalStatus = (client) => {
    if (client.user_id) {
      return {
        label: 'Portal Active',
        className: 'bg-green-50 text-green-700 border-green-100'
      };
    }

    if (client.invitation_status === 'invited') {
      return {
        label: 'Invited',
        className: 'bg-blue-50 text-blue-700 border-blue-100'
      };
    }

    return {
      label: 'Not Invited',
      className: 'bg-slate-50 text-slate-500 border-slate-200'
    };
  };

  useEffect(() => {
    const fetchClients = async () => {
      if (!profile?.advisor_firm_id) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchErr } = await supabase
          .from('clients')
          .select('*')
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .order('last_name', { ascending: true });

        if (fetchErr) {
          console.error('ClientList fetch error:', fetchErr);
          throw fetchErr;
        }

        setClients(data || []);
      } catch (err) {
        setError(err.message || "Failed to load client list.");
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [profile]);

  if (!profile || (profile.role !== 'advisor_admin' && profile.role !== 'advisor_user')) {
    return <div className="p-8 text-center bg-white rounded-lg border border-slate-200">Advisor access required.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
          <p className="text-sm text-slate-500">Manage your firm's estate planning prospects and clients.</p>
        </div>
        <button 
          onClick={() => navigate('/clients/create')}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiUserPlus} className="-ml-1 mr-2 h-5 w-5" />
          Add Client
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mx-auto h-10 w-10 text-slate-300 mb-4" />
            <p className="text-slate-500">Loading client records...</p>
          </div>
        ) : clients.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Residence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Added</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {clients.map((client) => {
                  const portalStatus = getPortalStatus(client);

                  return (
                    <tr
                      key={client.id}
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/clients/${client.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-900">
                          {client.first_name} {client.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">{client.email}</div>
                        <div className="text-xs text-slate-400">{client.phone || 'No phone'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {client.state_of_residence}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full border ${portalStatus.className}`}>
                          {portalStatus.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {format(new Date(client.created_at), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/matters/create?clientId=${client.id}`);
                            }}
                            className="text-slate-600 hover:text-slate-900 flex items-center bg-slate-100 px-2 py-1 rounded"
                            title="Create Plan"
                          >
                            <SafeIcon icon={FiIcons.FiPlusSquare} className="h-4 w-4 mr-1" />
                            <span className="text-xs font-bold uppercase tracking-tighter">Plan</span>
                          </button>
                          <button
                            className="text-slate-400 hover:text-slate-600"
                            title="View Details"
                          >
                            <SafeIcon icon={FiIcons.FiChevronRight} className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-20 text-center text-slate-500 px-4">
            <SafeIcon icon={FiIcons.FiUsers} className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No clients yet</h3>
            <p className="mt-1 text-slate-500">Add a client to start an estate plan.</p>
            <button 
              onClick={() => navigate('/clients/create')}
              className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900"
            >
              Add your first client
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientList;
